import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Tooltip, IconButton, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";

import useDebounce from "../hooks/useDebounce";
import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getReceipts } from "../redux/actions/receiptActions";
import { ReceiptSummaryDto } from "../shared/dtos/receipt/ReceiptSummaryDto";
import { formatPlainNumber } from "../utils/appUtils";

const ReceiptsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navigateToTab } = useWorkspaceNavigation();
  const { hasPermission } = usePermissions();

  const today = new Date().toISOString().split("T")[0];

  // Redux State
  const {
    items: receipts,
    pagination,
    loadingFetchAll: loadingReceipts,
    errorFetchAll: errorLoadingReceipts,
  } = useAppSelector((state) => state.receipt);

  // Local State
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const canRead = hasPermission("receipt:read_all");

  const fetchReceiptsList = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getReceipts({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    startDate,
    endDate,
    canRead,
  ]);

  useEffect(() => {
    fetchReceiptsList();
  }, [fetchReceiptsList]);

  // Reset to page 1 when search or dates change
  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, startDate, endDate]);

  const handleViewDetails = (receipt: ReceiptSummaryDto): void => {
    navigateToTab(`/receipts/${receipt.id}`);
  };

  // --- Table Configuration with Column Management ---
  const columns: TableColumn<ReceiptSummaryDto>[] = useMemo(
    () => [
      // --- DEFAULT VISIBLE COLUMNS ---
      {
        id: "transactionNumber",
        label: "Receipt #",
        minWidth: 120,
        format: (_, row) => (
          <b style={{ color: "#4cc9f0" }}>{row.transactionNumber}</b>
        ),
      },
      {
        id: "receiptDateTime",
        label: "Date & Time",
        minWidth: 150,
        format: (_, row) => new Date(row.receiptDateTime).toLocaleString(),
      },
      {
        id: "receiptType",
        label: "Type",
        minWidth: 90,
        format: (_, row) => <StatusChip status={row.receiptType} />,
      },
      {
        id: "transactionStatus",
        label: "Status",
        minWidth: 110,
        format: (_, row) => <StatusChip status={row.transactionStatus} />,
      },
      { id: "currencyCode", label: "CUR", minWidth: 60, align: "center" },
      {
        id: "total",
        label: "Amount",
        minWidth: 100,
        align: "right",
        format: (_, row) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {formatPlainNumber(row.total)}
          </Typography>
        ),
      },
      {
        id: "amountPaid",
        label: "Paid",
        minWidth: 100,
        align: "right",
        format: (_, row) => (
          <Typography
            variant="body2"
            color="success.main"
            sx={{ fontWeight: "bold" }}
          >
            {formatPlainNumber(row.amountPaid)}
          </Typography>
        ),
      },

      // --- HIDDEN BY DEFAULT ---
      {
        id: "changeAmount",
        label: "Change",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => (
          <Typography
            variant="body2"
            color="error.main"
            sx={{ fontWeight: "bold" }}
          >
            {formatPlainNumber(row.changeAmount)}
          </Typography>
        ),
      },
      {
        id: "saleType",
        label: "Sale Mode",
        minWidth: 100,
        defaultHidden: true,
        format: (_, row) => (
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {row.saleType}
          </Typography>
        ),
      },
      {
        id: "subtotal",
        label: "Subtotal",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.subtotal),
      },
      {
        id: "totalDiscount",
        label: "Discount",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalDiscount),
      },
      {
        id: "totalTaxableAmount",
        label: "Taxable",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalTaxableAmount),
      },
      {
        id: "totalTax",
        label: "Tax",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalTax),
      },
      {
        id: "exchangeRate",
        label: "Rate",
        minWidth: 80,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.exchangeRate),
      },
    ],
    [],
  );

  return (
    <PageContainer
      title="Transaction Receipts"
      subtitle="View and manage all cash and credit sales generated from the POS."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by receipt number..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={receipts}
        loading={loadingReceipts}
        error={errorLoadingReceipts}
        onRetry={fetchReceiptsList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig({ ...pageConfig, page: p })}
        onRowsPerPageChange={(l) =>
          setPageConfig({ ...pageConfig, limit: l, page: 1 })
        }
        enableColumnManagement={true}
        rowAction={(row) => (
          <Tooltip title="View Receipt / Print">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleViewDetails(row)}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      />
    </PageContainer>
  );
};

export default ReceiptsPage;
